import { Slot } from "@radix-ui/react-slot"
import type { ComponentPropsWithoutRef, ElementType, JSX } from "react"
import { cn } from "../lib/utils"

const typographyVariantClassNames = {
  "heading-h1":
    "ds-type-scale-3xl ds-type-weight-semibold ds-type-tracking-tight",
  "heading-h2":
    "ds-type-scale-heading-h2 ds-type-weight-bold ds-type-tracking-tight",
  "heading-h3": "ds-type-scale-xl ds-type-weight-bold ds-type-tracking-tight",
  "heading-h4": "ds-type-scale-lg ds-type-weight-bold ds-type-tracking-normal",
  "heading-h5":
    "ds-type-scale-base ds-type-weight-semibold ds-type-tracking-normal",
  "heading-h6":
    "ds-type-scale-sm ds-type-weight-semibold ds-type-tracking-normal",
  "text-xs-normal":
    "ds-type-scale-xs ds-type-weight-normal ds-type-tracking-normal",
  "text-xs-medium":
    "ds-type-scale-xs ds-type-weight-medium ds-type-tracking-normal",
  "text-xs-semibold":
    "ds-type-scale-xs ds-type-weight-semibold ds-type-tracking-normal",
  "text-xs-bold":
    "ds-type-scale-xs ds-type-weight-bold ds-type-tracking-normal",
  "text-xs-uppercase-normal":
    "ds-type-font-accent ds-type-scale-xs ds-type-weight-normal ds-type-uppercase ds-type-tracking-normal",
  "text-xs-uppercase-medium":
    "ds-type-font-accent ds-type-scale-xs ds-type-weight-medium ds-type-uppercase ds-type-tracking-normal",
  "text-sm-normal":
    "ds-type-scale-sm ds-type-weight-normal ds-type-tracking-normal",
  "text-sm-medium":
    "ds-type-scale-sm ds-type-weight-medium ds-type-tracking-normal",
  "text-sm-semibold":
    "ds-type-scale-sm ds-type-weight-semibold ds-type-tracking-normal",
  "text-sm-bold":
    "ds-type-scale-sm ds-type-weight-bold ds-type-tracking-normal",
  "text-base-normal":
    "ds-type-scale-base ds-type-weight-normal ds-type-tracking-normal",
  "text-base-medium":
    "ds-type-scale-base ds-type-weight-medium ds-type-tracking-normal",
  "text-base-semibold":
    "ds-type-scale-base ds-type-weight-semibold ds-type-tracking-normal",
  "text-base-bold":
    "ds-type-scale-base ds-type-weight-bold ds-type-tracking-normal",
  "text-lg-normal":
    "ds-type-scale-lg ds-type-weight-normal ds-type-tracking-normal",
  "text-lg-medium":
    "ds-type-scale-lg ds-type-weight-medium ds-type-tracking-normal",
  "text-lg-semibold":
    "ds-type-scale-lg ds-type-weight-semibold ds-type-tracking-normal",
  "text-lg-bold":
    "ds-type-scale-lg ds-type-weight-bold ds-type-tracking-normal",
  "text-xl-normal":
    "ds-type-scale-xl ds-type-weight-normal ds-type-tracking-normal",
  "text-xl-medium":
    "ds-type-scale-xl ds-type-weight-medium ds-type-tracking-normal",
  "text-xl-semibold":
    "ds-type-scale-xl ds-type-weight-semibold ds-type-tracking-normal",
  "text-xl-bold":
    "ds-type-scale-xl ds-type-weight-bold ds-type-tracking-normal",
  "text-2xl-normal":
    "ds-type-scale-2xl ds-type-weight-normal ds-type-tracking-normal",
  "text-2xl-medium":
    "ds-type-scale-2xl ds-type-weight-medium ds-type-tracking-normal",
  "text-2xl-semibold":
    "ds-type-scale-2xl ds-type-weight-semibold ds-type-tracking-normal",
  "text-2xl-bold":
    "ds-type-scale-2xl ds-type-weight-bold ds-type-tracking-normal",
  "text-3xl-normal":
    "ds-type-scale-3xl ds-type-weight-normal ds-type-tracking-normal",
  "text-3xl-medium":
    "ds-type-scale-3xl ds-type-weight-medium ds-type-tracking-normal",
  "text-3xl-semibold":
    "ds-type-scale-3xl ds-type-weight-semibold ds-type-tracking-normal",
  "text-3xl-bold":
    "ds-type-scale-3xl ds-type-weight-bold ds-type-tracking-normal",
  "text-4xl-normal":
    "ds-type-scale-4xl ds-type-weight-normal ds-type-tracking-normal",
  "text-4xl-medium":
    "ds-type-scale-4xl ds-type-weight-medium ds-type-tracking-normal",
  "text-4xl-semibold":
    "ds-type-scale-4xl ds-type-weight-semibold ds-type-tracking-normal",
  "text-4xl-bold":
    "ds-type-scale-4xl ds-type-weight-bold ds-type-tracking-normal",
} as const

const variantDefaultElements = {
  "heading-h1": "h1",
  "heading-h2": "h2",
  "heading-h3": "h3",
  "heading-h4": "h4",
  "heading-h5": "h5",
  "heading-h6": "h6",
  "text-xs-normal": "p",
  "text-xs-medium": "p",
  "text-xs-semibold": "p",
  "text-xs-bold": "p",
  "text-xs-uppercase-normal": "span",
  "text-xs-uppercase-medium": "span",
  "text-sm-normal": "p",
  "text-sm-medium": "p",
  "text-sm-semibold": "p",
  "text-sm-bold": "p",
  "text-base-normal": "p",
  "text-base-medium": "p",
  "text-base-semibold": "p",
  "text-base-bold": "p",
  "text-lg-normal": "p",
  "text-lg-medium": "p",
  "text-lg-semibold": "p",
  "text-lg-bold": "p",
  "text-xl-normal": "p",
  "text-xl-medium": "p",
  "text-xl-semibold": "p",
  "text-xl-bold": "p",
  "text-2xl-normal": "p",
  "text-2xl-medium": "p",
  "text-2xl-semibold": "p",
  "text-2xl-bold": "p",
  "text-3xl-normal": "p",
  "text-3xl-medium": "p",
  "text-3xl-semibold": "p",
  "text-3xl-bold": "p",
  "text-4xl-normal": "p",
  "text-4xl-medium": "p",
  "text-4xl-semibold": "p",
  "text-4xl-bold": "p",
} as const

const paragraphSpacingClassNames = {
  none: "ds-type-paragraph-none",
  body: "ds-type-paragraph-body",
} as const

const wrapperTypographyClassNames = {
  badge:
    "ds-type-font-accent ds-type-scale-xs ds-type-weight-semibold ds-type-tracking-normal",
  badgeSecondary:
    "ds-type-font-accent ds-type-scale-xs ds-type-weight-normal ds-type-tracking-normal",
  button:
    "ds-type-font-sans ds-type-scale-sm ds-type-weight-medium ds-type-tracking-normal",
  fieldLabel:
    "ds-type-font-sans ds-type-scale-sm ds-type-weight-medium ds-type-tracking-normal",
  input: "ds-type-control-input",
  selectItem:
    "ds-type-font-sans ds-type-scale-sm ds-type-weight-normal ds-type-tracking-normal",
  selectLabel:
    "ds-type-font-sans ds-type-scale-sm ds-type-weight-semibold ds-type-tracking-normal",
  selectTrigger:
    "ds-type-font-sans ds-type-scale-sm ds-type-weight-normal ds-type-tracking-normal",
  toastDescription:
    "ds-type-font-sans ds-type-scale-sm ds-type-weight-normal ds-type-tracking-normal",
  toastTitle:
    "ds-type-font-sans ds-type-scale-sm ds-type-weight-semibold ds-type-tracking-normal",
  tooltip:
    "ds-type-font-sans ds-type-scale-xs ds-type-weight-normal ds-type-tracking-normal",
} as const

type TypographyOwnProps<T extends ElementType> = {
  asChild?: boolean
  as?: T
  paragraphSpacing?: TypographyParagraphSpacing
  variant?: TypographyVariant
}

export type TypographyVariant = keyof typeof typographyVariantClassNames
export type TypographyParagraphSpacing = keyof typeof paragraphSpacingClassNames
export type TypographyElement = keyof JSX.IntrinsicElements
export type WrapperTypographyKey = keyof typeof wrapperTypographyClassNames
export type TypographyProps<T extends ElementType = "p"> =
  TypographyOwnProps<T> &
    Omit<ComponentPropsWithoutRef<T>, keyof TypographyOwnProps<T>>

export const typographyVariants = Object.keys(
  typographyVariantClassNames
) as TypographyVariant[]

export function Typography<T extends ElementType = "p">({
  as,
  asChild = false,
  className,
  paragraphSpacing = "none",
  variant = "text-base-normal",
  ...props
}: TypographyProps<T>) {
  const Comp = (
    asChild ? Slot : (as ?? variantDefaultElements[variant])
  ) as ElementType

  return (
    <Comp
      data-slot="typography"
      className={cn(
        "ds-type-font-sans",
        typographyVariantClassNames[variant],
        paragraphSpacingClassNames[paragraphSpacing],
        className
      )}
      {...props}
    />
  )
}

export {
  paragraphSpacingClassNames,
  typographyVariantClassNames,
  wrapperTypographyClassNames,
}
